<script setup lang="ts">
import loader from '@monaco-editor/loader';
import { shikiToMonaco } from '@shikijs/monaco';
import type { IDisposable, IEditor, ICompletion, IRange } from './types';
import type { LanguageOption } from './languages';
import { getHighlighter } from './highlighter';

const props = withDefaults(defineProps<{
    language: LanguageOption;
    height?: string;
    placeholder?: string;
    nospace?: boolean;
    border?: boolean;
    stretch?: boolean;
    tabbed?: boolean;
    readonly?: boolean;
    autoLayout?: boolean;
    disableMinimap?: boolean;
    disableLineNumbers?: boolean;
    completion?: (word: string, textBefore: string, range: IRange) => Promise<ICompletion[]>;
}>(), {
    height: '300px',
    nospace: false,
    border: true,
    stretch: false,
    tabbed: false,
    readonly: false,
    autoLayout: false,
    disableMinimap: false,
    disableLineNumbers: false
});

const modelValue = defineModel<string>('modelValue', { default: '' });
const monacoRef = ref<HTMLDivElement | null>(null);
const theme = computed(() => useThemeStore().theme === 'dark' ? 'github-dark' : 'github-light');

let editor: IEditor | null = null;
let provider: IDisposable | null = null;
let setTheme: (themeName: string) => void = null;

function flag(value: any)
{
    return !!value;
}

async function formatDocument()
{
    await editor?.getAction('editor.action.formatDocument')?.run();
}

function changeOption(options: Record<string, any>)
{
    editor?.updateOptions(options);
}

onMounted(async () =>
{
    loader.config({
        'paths': {
            vs: `${window.location.origin}/monaco/min/vs`
        },
        'vs/nls': {
            availableLanguages: {
                '*': 'en'
            }
        }
    });

    loader.init().then(async (monaco) =>
    {
        const highlighter = await getHighlighter();

        // Monaco domyślnie nie zna Vue więc trzeba go zarejestrować
        if (props.language == 'vue')
        {
            monaco.languages.register({ id: props.language });
        }

        // UWAGA: Wyłączenie walidacji JSON. Specyficzny przypadek dla formularzy dynamicznych. Nie zmieniać!
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: false
        });

        setTheme = monaco.editor.setTheme;
        shikiToMonaco(highlighter, monaco);

        // Rejestracja providera podpowiadania
        provider = monaco.languages.registerCompletionItemProvider(props.language, {
            provideCompletionItems: async (model: any, position: any) =>
            {
                // Sprawdzenie czy sugestie są dla naszego edytora
                if (props.completion && editor && editor.getModel() == model)
                {
                    // Definiowanie parametrów
                    const word = model.getWordUntilPosition(position);
                    const textBefore = model.getValueInRange({
                        startLineNumber: 1,
                        startColumn: 1,
                        endLineNumber: position.lineNumber,
                        endColumn: position.column
                    });
                    const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn
                    };

                    // Pobranie i zwrócenie sugestii
                    const items = await props.completion(word.word, textBefore, range);

                    return {
                        suggestions: items as any
                    };
                }

                return { suggestions: [] };
            }
        });

        editor = monaco.editor.create(monacoRef.value, {
            value: modelValue.value,
            language: props.language,
            theme: theme.value,
            placeholder: props.placeholder,
            readOnly: props.readonly,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: props.autoLayout,
            quickSuggestions: true,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
            lineNumbers: props.disableLineNumbers ? 'off' : 'on',
            glyphMargin: !props.disableLineNumbers,
            lineDecorationsWidth: props.disableLineNumbers ? 0 : undefined,
            lineNumbersMinChars: props.disableLineNumbers ? 0 : undefined,
            folding: !props.disableLineNumbers,
            minimap: {
                enabled: !props.disableMinimap
            },
            parameterHints: {
                enabled: true
            },
            suggest: {
                showWords: false
            }
        });

        editor.onDidChangeModelContent(() =>
        {
            modelValue.value = editor?.getValue() || '';
        });

        editor.onDidDispose(() =>
        {
            provider?.dispose();
            setTheme = null;
        });
    });
});

watch(theme, (newTheme) =>
{
    if (editor)
    {
        setTheme?.(newTheme);
    }
});

watch(modelValue, (newValue) =>
{
    if (editor && editor.getValue() !== newValue)
    {
        editor.setValue(newValue);
    }
});

onBeforeUnmount(() =>
{
    editor?.dispose();
});

defineExpose({ formatDocument, changeOption });
</script>

<template>
    <div
        :class="{
            'border': flag(props.border),
            'border-top-0': flag(props.tabbed),
            'h-100': flag(props.stretch),
            'mb-3': !flag(props.nospace),
        }"
    >
        <div ref="monacoRef" class="monaco" />
    </div>
</template>

<style scoped>
.monaco {
    height: 100%;
    min-height: v-bind(height);
}
</style>
